// The cockpit's HUD feed: KillSwitch state and Core's summary (task counts,
// the usage ledger), re-read on an interval and shared by every admin view
// (docs/plan/phase-10-ui.md's admin views, extended).
//
// The point of it being a hook rather than a per-page fetch is that the
// HUD is on every admin page: one poller, one set of numbers, no view
// showing a stale balance while the one next to it is current. It only
// runs for a signed-in admin -- a non-admin never mounts the cockpit, but
// the guard costs nothing and keeps the requests honest.
import { useEffect, useRef, useState } from "react";
import { adminSummary, type AdminSummary } from "../api/client";
import { fetchKillSwitchState, taskCounts, type KillSwitchState, type TaskCounts } from "../api/admin";

export interface AdminStatus {
  killswitch: KillSwitchState | null;
  summary: AdminSummary | null;
  counts: TaskCounts | null;
  /** Wall-clock time of the last refresh in which every source answered,
   * for the "updated" readout: a HUD that does not say when it last heard
   * from the control plane is worse than no HUD, because it looks live
   * when it is not. A failed refresh leaves it where it was. */
  updatedAt: number | null;
  error: string | null;
  refresh: () => void;
}

export const ADMIN_STATUS_INTERVAL_MS = 5000;

export function useAdminStatus(enabled: boolean, intervalMs = ADMIN_STATUS_INTERVAL_MS): AdminStatus {
  const [killswitch, setKillswitch] = useState<KillSwitchState | null>(null);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // One in-flight refresh at a time: a slow control plane must not stack
  // up requests every interval.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function load() {
      if (inFlight.current) return;
      inFlight.current = true;
      const [ks, sm] = await Promise.allSettled([fetchKillSwitchState(), adminSummary()]);
      inFlight.current = false;
      if (cancelled) return;

      // Each source lands independently: a KillSwitch that is down must not
      // blank the queue counts, and vice versa.
      const failures: string[] = [];
      if (ks.status === "fulfilled") setKillswitch(ks.value);
      else failures.push(message(ks.reason));
      if (sm.status === "fulfilled") setSummary(sm.value);
      else failures.push(message(sm.reason));

      setError(failures.length > 0 ? failures.join("; ") : null);
      if (failures.length === 0) setUpdatedAt(Date.now());
    }

    void load();
    const timer = setInterval(() => void load(), intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, intervalMs, nonce]);

  return {
    killswitch,
    summary,
    counts: summary ? taskCounts(summary) : null,
    updatedAt,
    error,
    refresh: () => setNonce((n) => n + 1),
  };
}

function message(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  return "admin status unavailable";
}

/** The KillSwitch cell's one-word state, and the tone the HUD paints it
 * with. `unknown` is not decoration: until the first response arrives we
 * genuinely do not know, and a green "ARMED" that has not been confirmed
 * is the single most dangerous thing this HUD could show. */
export function killSwitchLabel(ks: KillSwitchState | null): { text: string; tone: "ok" | "bad" | "unknown"; detail: string } {
  if (!ks) return { text: "···", tone: "unknown", detail: "no contact" };
  if (ks.tripped) {
    return { text: "TRIPPED", tone: "bad", detail: ks.reason || "tripped" };
  }
  return { text: "ARMED", tone: "ok", detail: ks.reason || "armed" };
}

/** A count as the HUD prints it. */
export function countLabel(n: number | undefined): string {
  return n === undefined ? "···" : String(n);
}

/** What the provider has charged us: RunPod (and Vast, reserved) spend as
 * KillSwitch measures it from the account balance. User charges are not
 * spend -- they are what the balance adds back. */
export function providerSpend(ks: KillSwitchState | null): number | undefined {
  if (!ks?.inputs) return undefined;
  return Math.max(ks.inputs.runpod_spent_total_minor ?? 0, 0) + Math.max(ks.inputs.vast_spent_total_minor ?? 0, 0);
}

/** The funds KillSwitch lets burn: budget plus what users were billed
 * (06-killswitch.md §1: balance = budget + billed − provider spend). */
export function fundsTotal(ks: KillSwitchState | null): number | undefined {
  const budget = ks?.inputs?.budget_total_minor;
  if (typeof budget !== "number") return undefined;
  return budget + Math.max(ks?.inputs?.billed_total_minor ?? 0, 0);
}

/** Share of the funds already spent at the provider, clamped for a gauge:
 * 1 is exactly where KillSwitch trips (balance 0). Undefined when the
 * funds are unknown or zero, where a percentage would be meaningless. */
export function budgetUsedFraction(ks: KillSwitchState | null): number | undefined {
  const funds = fundsTotal(ks);
  const spent = providerSpend(ks);
  if (funds === undefined || funds <= 0 || spent === undefined) return undefined;
  return Math.min(1, spent / funds);
}

/** Seconds since an RFC 3339 time, or undefined if absent or unparsable. */
export function ageSeconds(at: string | null | undefined, now = Date.now()): number | undefined {
  if (!at) return undefined;
  const t = Date.parse(at);
  return Number.isNaN(t) ? undefined : Math.max(0, Math.round((now - t) / 1000));
}

/** KillSwitch re-reads its inputs every 30 s while armed; older than this
 * means its numbers are stale (tripped, or the poll is failing). */
export const KILLSWITCH_STALE_S = 120;

/** "read 12 s ago" / "read 3 min ago" -- how old a KillSwitch reading is. */
export function ageLabel(seconds: number | undefined): string {
  if (seconds === undefined) return "never read";
  if (seconds < 90) return `read ${seconds} s ago`;
  if (seconds < 5400) return `read ${Math.round(seconds / 60)} min ago`;
  return `read ${Math.round(seconds / 3600)} h ago`;
}
